import React from 'react';

// cleanUp called to clear transition timers
type cleanUp = () => void;

interface Context extends State {
    // setExited could be used to manually trigger exit
    // Useful if we want to hook into onTransitionEnd event
    // rather than using a set timer delay
    setExited: () => void;

    // enableTransition enables transition for the route
    // This should only be called once for each route.
    // Ideally at the page layout level where it's closer to the page transition
    // This should be called inside useEffect because it mutates the states
    // and it returns a cleanUp callback for useEffect
    enableTransition: (exitDelay?: number) => cleanUp | void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const routeTransitionContext = React.createContext<Context>(null as any);

interface Props {
    location: string;
    children: React.ReactNode;
}

export enum TransitionState {
    // The state just after mount and before transition in starts
    From = 'from',

    // The State when enter transition starts and ends
    Enter = 'enter',

    // The State when transition out begins
    // After transition out is complete the next location will get mounted
    // and the state goes back to TransitionState.From
    Leave = 'leave',
}

interface State {
    transitionState: TransitionState;
    nextLocation: string | null;
    activeLocation: string;
    previousLocation: string | null;
}

export function useRouteTransition(): Context {
    return React.useContext(routeTransitionContext);
}

/**
 * RouteTransitionProvider is a component that listens and manages the change of route location
 * to provide its children with a set of states for transitions.
 */
export class RouteTransitionProvider extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            transitionState: TransitionState.From,
            nextLocation: null,
            activeLocation: props.location,
            previousLocation: null,
        };
        this.enableTransition = this.enableTransition.bind(this);
        this.setExited = this.setExited.bind(this);
    }

    static getDerivedStateFromProps(
        { location: targetLocation }: Props,
        { activeLocation, transitionState, previousLocation }: State
    ) {
        if (targetLocation !== activeLocation) {
            if (transitionState === TransitionState.Enter) {
                return {
                    transitionState: TransitionState.Leave,
                    previousLocation: previousLocation,
                    activeLocation,
                    nextLocation: targetLocation,
                };
            }

            return {
                transitionState: TransitionState.From,
                previousLocation: activeLocation,
                activeLocation: targetLocation,
                nextLocation: null,
            };
        }

        return null;
    }

    triggerEnterTransition() {
        // Needs to set a timeout here because enter state needs to be triggered
        // after the initial render is complete
        const timeId = setTimeout(() => {
            this.setState({
                ...this.state,
                transitionState: TransitionState.Enter,
            });
        }, 20);

        return () => clearTimeout(timeId);
    }

    setExited() {
        this.setState({
            transitionState: TransitionState.From,
            previousLocation: this.state.activeLocation,
            activeLocation: this.state.nextLocation as string,
            nextLocation: null,
        });
    }

    exitAfterDelay(delay: number) {
        const timeId = setTimeout(() => {
            this.setExited();
        }, delay);
        return () => clearTimeout(timeId);
    }

    enableTransition(exitDelay?: number) {
        const { transitionState } = this.state;

        if (transitionState === TransitionState.From) {
            return this.triggerEnterTransition();
        }

        if (transitionState === TransitionState.Leave && exitDelay) {
            return this.exitAfterDelay(exitDelay);
        }
    }

    render() {
        return (
            <routeTransitionContext.Provider
                value={{
                    ...this.state,
                    setExited: this.setExited,
                    enableTransition: this.enableTransition,
                }}
            >
                {this.props.children}
            </routeTransitionContext.Provider>
        );
    }
}
